import React, { ChangeEvent, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CreateRoadmapItemResponse,
  RoadmapDto,
  RoadmapItemDto,
  RoadmapItemSourceType,
} from "@lib/ShiOneClient";
import CloseIcon from "@mui/icons-material/Close";
import useGetRoadmapsByCustomerId from "shared-ui/src/features/roadmaps-feature/hooks/useGetRoadmapsByCustomerId";
import SendToDialogRoadmapsView from "@features/assessments-feature/components/priorities-recommendations/SendToDialogRoadmapsView";
import SendToDialogInitiativesView from "@features/assessments-feature/components/priorities-recommendations/SendToDialogInitiativesView";
import LoadingOrError from "shared-ui/src/global-layout/LoadingOrError";
import SnackbarMessage from "shared-ui/src/theme/extensions/SnackbarMessage";
import useCreateRoadmapItem from "shared-ui/src/features/roadmaps-feature/hooks/useCreateRoadmapItem";
import { initialRoadmapItemState } from "shared-ui/src/features/roadmaps-feature/utils/roadmapProps";

const SendToDialog = ({
  selectedSourceIds,
  isOpenSendToDialog,
  setIsOpenSendToDialog,
}: {
  selectedSourceIds: Map<string, string>;
  isOpenSendToDialog: boolean;
  setIsOpenSendToDialog: (open: boolean) => void;
}) => {
  const theme = useTheme();

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [selectedRoadmap, setSelectedRoadmap] = useState<RoadmapDto | null>(
    null,
  );
  const [selectedInitiative, setSelectedInitiative] =
    useState<RoadmapItemDto | null>(null);
  const [currentView, setCurrentView] = useState<
    "roadmapsView" | "initiativesView"
  >("roadmapsView");

  const { data: roadmaps, isLoading, isError } = useGetRoadmapsByCustomerId();

  const { mutate: createNewRoadmapItem } =
    useCreateRoadmapItem(setShowSnackbar);

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };
  const handleRoadmapClick = (roadmap: RoadmapDto) => {
    setSelectedRoadmap(roadmap);
    setCurrentView("initiativesView");
  };

  const handleInitiativeClick = (initiative: RoadmapItemDto | null) => {
    selectedInitiative?.roadmapItemId === initiative?.roadmapItemId
      ? setSelectedInitiative(null)
      : setSelectedInitiative(initiative);
  };

  const handleChangeNewInitiativeName = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSelectedInitiative({
      ...selectedInitiative,
      name: event.target.value,
    } as RoadmapItemDto);
  };

  const handleBackToRoadmaps = () => {
    setCurrentView("roadmapsView");
    setSelectedRoadmap(null);
    setSelectedInitiative(null);
  };

  const handleSubmitNewRoadmapItemFromAssessments = () => {
    const createTasksCallbackFunction = (parentRoadmapItem: RoadmapItemDto) => {
      selectedSourceIds.forEach((capability, sourceId) => {
        const newChildRoadmapItem = {
          sourceId: sourceId,
          sourceType: RoadmapItemSourceType.AssessmentRecommendation,
          parentRoadmapItemId: parentRoadmapItem.roadmapItemId,
          roadmapId: selectedRoadmap?.id,
          name: capability,
        } as RoadmapItemDto;

        createNewRoadmapItem(newChildRoadmapItem, {
          onSuccess: (callbackFunctionResponse: CreateRoadmapItemResponse) => {
            if (callbackFunctionResponse.isSuccessful === true) {
              setIsOpenSendToDialog(false);
            } else setShowSnackbar(true);
          },
          onError: () => {
            setShowSnackbar(true);
          },
        });
      });
    };

    if (selectedInitiative?.roadmapItemId === "0") {
      const newParentRoadmapItem = {
        ...initialRoadmapItemState,
        roadmapId: selectedRoadmap?.id,
        name: selectedInitiative?.name,
      } as RoadmapItemDto;

      createNewRoadmapItem(newParentRoadmapItem, {
        onSuccess: ({
          isSuccessful,
          roadmapItem,
        }: CreateRoadmapItemResponse) => {
          if (isSuccessful && roadmapItem) {
            createTasksCallbackFunction(roadmapItem);
          } else {
            setShowSnackbar(true);
          }
        },
        onError: () => setShowSnackbar(true),
      });
    } else if (selectedInitiative) {
      createTasksCallbackFunction(selectedInitiative);
    }
  };

  if (isLoading) {
    return <LoadingOrError loading={isLoading} />;
  }

  const renderCurrentView = () => {
    if (isError) {
      return (
        <LoadingOrError
          error={isError}
          message="There's a problem loading roadmaps data"
          suggestions="Please try again later."
        />
      );
    }
    return (
      <>
        <DialogContent dividers sx={{ p: 0, height: "450px" }}>
          {currentView === "roadmapsView" && (
            <SendToDialogRoadmapsView
              roadmaps={roadmaps.roadmapList!}
              setShowSnackbar={setShowSnackbar}
              handleRoadmapClick={handleRoadmapClick}
            />
          )}

          {currentView === "initiativesView" && (
            <SendToDialogInitiativesView
              selectedRoadmap={selectedRoadmap}
              selectedInitiative={selectedInitiative}
              handleInitiativeClick={handleInitiativeClick}
              handleChangeNewInitiativeName={handleChangeNewInitiativeName}
            />
          )}
        </DialogContent>
        {currentView === "initiativesView" && (
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              variant="text"
              sx={{ color: theme.palette.common.black }}
              autoFocus
              onClick={handleBackToRoadmaps}
            >
              Back
            </Button>
            <Button onClick={handleSubmitNewRoadmapItemFromAssessments}>
              Send
            </Button>
          </DialogActions>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog
        open={isOpenSendToDialog}
        onClose={() => {
          setIsOpenSendToDialog(false);
        }}
        fullWidth
        maxWidth={"sm"}
      >
        <DialogTitle>
          <Typography variant={"body1"}>Send To Roadmap Initiative </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setIsOpenSendToDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        {renderCurrentView()}
      </Dialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <SnackbarMessage
          variant={"error"}
          message={
            "There was an issue creating the item, please try again later."
          }
        />
      </Snackbar>
    </>
  );
};

export default SendToDialog;
